import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { Volume2, Play, CheckCircle2, AlertTriangle, RotateCw, Loader2, Database, Download } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; }
};

const LANG_LABEL = { en: 'English', ta: 'Tamil', hi: 'Hindi', ne: 'Nepali', kn: 'Kannada', ml: 'Malayalam', bn: 'Bengali', mr: 'Marathi', te: 'Telugu' };

/**
 * Admin-only pre-warm panel: kicks off background audio generation for every
 * ready SOP translation and shows live progress bar.
 */
export default function AudioPrewarmPanel() {
  const [stats, setStats] = useState(null);
  const [state, setState] = useState(null);
  const [cron, setCron] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  // Per-language download stats so we can render small per-language buttons
  // instead of a single 8-GB button that would time out through the ingress.
  const [exportStats, setExportStats] = useState(null);
  const pollRef = useRef(null);

  const loadAll = async () => {
    try {
      const [s, j, c, e] = await Promise.all([
        api.get('/sop-translations/audio/stats'),
        api.get('/sop-translations/audio/prewarm/status'),
        api.get('/sop-translations/audio/prewarm/cron-status').catch(() => ({ data: {} })),
        api.get('/sop-translations/audio/export-zip/stats').catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setState(j.data);
      setCron(c.data || {});
      setExportStats(e.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll every 2s while running
  useEffect(() => {
    if (state?.status === 'running') {
      if (!pollRef.current) {
        pollRef.current = setInterval(loadAll, 2000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status]);

  const startPrewarm = async () => {
    setBusy('start'); setError('');
    try {
      const r = await api.post('/sop-translations/audio/prewarm');
      if (r.data.already_running) setError('A pre-warm is already running.');
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Start failed');
    } finally { setBusy(''); }
  };

  const reset = async () => {
    if (!window.confirm('Reset pre-warm state? (use only if a prior run is stuck)')) return;
    try { await api.post('/sop-translations/audio/prewarm/reset'); await loadAll(); } catch {}
  };

  const dedupeExisting = async () => {
    if (!window.confirm('Dedupe existing audio?\n\nThis scans every translation and links duplicates (same content + language) to a single shared mp3. Saves ElevenLabs credits for every future pre-warm run.\n\nSafe — existing mp3s are preserved, just linked.')) return;
    setBusy('dedupe');
    try {
      const r = await api.post('/sop-translations/audio/dedupe-existing');
      const d = r.data || {};
      alert(`Dedup complete ✅\n\n• Rows linked: ${d.rows_linked}\n• Duplicate files removed: ${d.gridfs_files_deleted}\n• Groups found: ${d.groups_deduped}\n\nRe-run 'Pre-warm all remaining' to regenerate the freed capacity.`);
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Dedup failed');
    } finally { setBusy(''); }
  };

  if (loading) return <div className="text-xs py-6 text-center" style={{ color: '#9CA3AF' }}>Loading…</div>;

  const total = state?.total ?? 0;
  const done = state?.done ?? 0;
  const failed = state?.failed ?? 0;
  const pct = total ? Math.round((done + failed) * 100 / total) : 0;
  const running = state?.status === 'running';
  const isDone = state?.status === 'done';

  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }} data-testid="audio-prewarm-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Volume2 size={18} style={{ color: '#2A7B88' }} />
          <div>
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Translation Audio (ElevenLabs)</h2>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Pre-generate mp3 narration for every translated SOP so staff hear audio instantly on tap.
              Background job — safe to close this page, progress persists. Audio files live in GridFS
              (<code className="text-[10px]" style={{ color: '#2A7B88' }}>sop_audio</code>) and are automatically
              included in every Snapshot backup above.
            </p>
          </div>
        </div>
        <button onClick={loadAll}
          className="px-2.5 py-1.5 rounded-md border text-[11px] flex items-center gap-1"
          style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="audio-refresh">
          <RotateCw size={11} /> Refresh
        </button>
      </div>

      {/* Coverage stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Translations total</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{stats?.total_translations ?? 0}</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>With audio</p>
          <p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{stats?.with_audio ?? 0}</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Coverage</p>
          <p className="text-lg font-bold" style={{ color: (stats?.coverage_pct ?? 0) >= 99 ? '#2D8B5E' : '#BA7517' }}>{stats?.coverage_pct ?? 0}%</p>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Storage used</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{stats?.total_mb ?? 0} MB</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t mb-3" style={{ borderColor: '#E5E1DC' }}>
        <button onClick={startPrewarm} disabled={busy === 'start' || running}
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#2A7B88 0%,#1B2A4A 100%)' }}
          data-testid="audio-prewarm-start">
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          {running ? 'Running…' : (stats?.coverage_pct ?? 0) >= 100 ? 'Re-run pre-warm' : 'Pre-warm all remaining'}
        </button>
        {state?.status && state.status !== 'idle' && state.status !== 'done' && (
          <button onClick={reset} className="px-3 py-2 rounded-md border text-xs font-medium" style={{ borderColor: '#D4726A', color: '#D4726A', backgroundColor: '#FEF2F2' }} data-testid="audio-reset-btn">
            Reset state (stuck? click here)
          </button>
        )}
        {(stats?.with_audio ?? 0) > 0 && (
          <button onClick={dedupeExisting} disabled={busy === 'dedupe'}
            className="px-3 py-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: '#F5C86B', color: '#8C560F', backgroundColor: '#FFFBEB' }}
            title="One-time cleanup: for SOPs that share identical content across outlets, link them to a single shared mp3 (saves credits, reclaims storage)."
            data-testid="audio-dedupe-btn">
            {busy === 'dedupe' ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
            {busy === 'dedupe' ? 'Deduping…' : 'Dedupe duplicates'}
          </button>
        )}
        <p className="text-[11px] ml-2" style={{ color: '#9CA3AF' }}>
          ~26 sec per SOP · runs in background · only missing ones are processed
        </p>
      </div>

      {/* Per-language download row — prevents 8 GB single-ZIP timeouts */}
      {Array.isArray(exportStats?.items) && exportStats.items.length > 0 && (
        <div className="pt-3 border-t mb-3" style={{ borderColor: '#E5E1DC' }} data-testid="audio-export-by-lang">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-[11px] font-semibold" style={{ color: '#1B2A4A' }}>
              <Download size={11} className="inline mr-1" style={{ color: '#2A7B88' }} />
              Download audio as ZIP
              <span className="ml-1 font-normal" style={{ color: '#9CA3AF' }}>
                · split by language for reliable downloads
              </span>
            </p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
              Total: {exportStats.total_files} files · {(exportStats.total_mb / 1024).toFixed(2)} GB
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {exportStats.items.map((it) => (
              <a
                key={it.lang}
                href={`${api.defaults.baseURL}/sop-translations/audio/export-zip?lang=${it.lang}&token=${encodeURIComponent(localStorage.getItem('access_token') || '')}`}
                className="px-2.5 py-1.5 rounded-md border text-[11px] font-medium inline-flex items-center gap-1.5"
                style={{ borderColor: '#2A7B88', color: '#2A7B88', backgroundColor: '#F0FAFB' }}
                title={`Download every ${it.label} mp3 as a single ZIP (${it.files} files · ${it.mb} MB).`}
                data-testid={`audio-export-zip-${it.lang}`}
              >
                <Download size={11} /> {it.label}
                <span className="font-normal" style={{ color: '#6B7280' }}>
                  {it.files} · {it.mb >= 1024 ? `${(it.mb / 1024).toFixed(2)} GB` : `${it.mb} MB`}
                </span>
              </a>
            ))}
            {exportStats.total_mb < 3000 && (
              <a
                href={`${api.defaults.baseURL}/sop-translations/audio/export-zip?token=${encodeURIComponent(localStorage.getItem('access_token') || '')}`}
                className="px-2.5 py-1.5 rounded-md border text-[11px] font-medium inline-flex items-center gap-1.5"
                style={{ borderColor: '#1B2A4A', color: '#1B2A4A', backgroundColor: '#FFF' }}
                title="Everything in one ZIP. Only shown when the total is under 3 GB — larger exports should use per-language buttons to avoid timeouts."
                data-testid="audio-export-zip-all">
                <Download size={11} /> All languages (single ZIP)
              </a>
            )}
          </div>
          {exportStats.total_mb >= 3000 && (
            <p className="text-[10px] mt-1.5 pl-0.5" style={{ color: '#9CA3AF' }}>
              💡 Single-ZIP option hidden because total exceeds 3 GB — browser/proxy timeouts become unreliable above that size. Use per-language buttons above.
            </p>
          )}
        </div>
      )}

      {/* Nightly cron status */}
      <div className="rounded-lg border px-3 py-2 mb-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }} data-testid="audio-cron-status">
        <div className="flex items-center gap-2 text-[11px]" style={{ color: '#6B7280' }}>
          <RotateCw size={11} style={{ color: '#2A7B88' }} />
          <span><strong style={{ color: '#1B2A4A' }}>Nightly cron:</strong> runs every day at 02:00 IST (hard-capped 200 SOPs/night)</span>
        </div>
        <div className="text-[10px]" style={{ color: '#6B7280' }}>
          {cron?.last_run_at ? (
            <>Last ran {fmtDate(cron.last_run_at)} · processed <strong style={{ color: '#2D8B5E' }}>{cron.last_run_processed ?? 0}</strong>
              {cron.last_run_failed > 0 && <span style={{ color: '#D4726A' }}> · failed {cron.last_run_failed}</span>}
              {cron.last_run_pending_remaining > 0 && <span> · {cron.last_run_pending_remaining} queued for next run</span>}
            </>
          ) : <em>has not run yet — first run tonight at 02:00 IST</em>}
        </div>
      </div>

      {/* Live progress */}
      {state?.status && state.status !== 'idle' && (
        <div className="rounded-lg border p-4" style={{ borderColor: running ? '#2A7B88' : isDone ? '#2D8B5E' : '#E5E1DC', backgroundColor: running ? '#F0FAFB' : isDone ? '#ECFDF5' : '#F9FAFB' }} data-testid="audio-prewarm-progress">
          <div className="flex items-center gap-2 mb-2">
            {running ? <Loader2 size={14} className="animate-spin" style={{ color: '#2A7B88' }} /> :
              isDone ? <CheckCircle2 size={14} style={{ color: '#2D8B5E' }} /> :
              <Database size={14} style={{ color: '#6B7280' }} />}
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
              {running ? `Pre-warming audio… ${done + failed}/${total}` :
                isDone ? `Completed — ${done} generated, ${failed} failed` :
                `Status: ${state.status}`}
            </p>
          </div>
          {/* Progress bar */}
          <div className="h-2 rounded-full overflow-hidden mb-2" style={{ backgroundColor: '#E5E1DC' }}>
            <div className="h-full transition-all duration-500" style={{
              width: `${pct}%`,
              background: failed > 0 ? 'linear-gradient(90deg, #2A7B88 0%, #2A7B88 100%)' : '#2A7B88',
            }} />
          </div>
          <div className="flex items-center justify-between text-[11px]" style={{ color: '#6B7280' }}>
            <span>
              ✅ {done} done
              {failed > 0 && <span className="ml-2" style={{ color: '#D4726A' }}>⚠ {failed} failed</span>}
            </span>
            <span>
              {running && state.current_lang && `now: ${LANG_LABEL[state.current_lang] || state.current_lang}`}
              {!running && state.started_at && ` · started ${fmtDate(state.started_at)}`}
              {state.finished_at && ` · finished ${fmtDate(state.finished_at)}`}
            </span>
          </div>

          {/* Error log */}
          {(state.errors || []).length > 0 && (
            <details className="mt-3">
              <summary className="text-[11px] cursor-pointer font-semibold" style={{ color: '#D4726A' }}>
                <AlertTriangle size={11} className="inline mr-1" />
                {state.errors.length} recent error(s) — click to expand
              </summary>
              <div className="mt-2 space-y-1 text-[10px] font-mono" style={{ color: '#6B7280' }}>
                {state.errors.slice(-10).map((err, i) => (
                  <div key={i} className="truncate">
                    [{LANG_LABEL[err.lang] || err.lang}] {err.translation_id?.slice(-8)}: {err.error}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-md p-2.5 text-[11px] flex items-center gap-1.5" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
          <AlertTriangle size={12} /> {error}
        </div>
      )}
    </div>
  );
}
